import { companyRequest } from '@/company/api';
import { displayName } from './display-name';
import type {
  AgentAccess,
  ApprovalAction,
  ApprovalActions,
  ApprovalConnection,
  ApprovalGroup,
  ApprovalMode,
  ApprovalPatch,
  FamilyApproval,
  AgentAccessScopeInfo,
  AgentAddress,
  CreatedAccessKey,
  KnowledgeClientGrant,
} from './types';

// Every response is validated before it reaches a component: a shape this page
// does not understand becomes a visible error, never a crash or a guess.
export class AgentResponseError extends Error {
  constructor() {
    // The page shows its own words for this (`agentErrorUnreadable`); the message is for logs.
    super('Poneva returned a response this page could not read.');
  }
}

type Json = Record<string, unknown>;
const isObject = (value: unknown): value is Json =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const object = (value: unknown): Json => {
  if (!isObject(value)) throw new AgentResponseError();
  return value;
};
const text = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value : null;
const required = (value: unknown): string => {
  const result = text(value);
  if (result === null) throw new AgentResponseError();
  return result;
};
const listOf = (value: unknown, key: string): unknown[] => {
  if (Array.isArray(value)) return value;
  const inner = isObject(value) ? value[key] : undefined;
  if (!Array.isArray(inner)) throw new AgentResponseError();
  return inner;
};
const timestamp = (value: unknown): string | null => {
  const result = text(value);
  return result && Number.isFinite(Date.parse(result)) ? result : null;
};

/** An http(s) URL with nothing around it. */
function httpUrl(value: unknown): string | null {
  const candidate = text(value);
  if (!candidate || candidate !== candidate.trim() || /\s/u.test(candidate)) return null;
  try {
    const url = new URL(candidate);
    return url.protocol === 'https:' || url.protocol === 'http:' ? candidate : null;
  } catch {
    return null;
  }
}

/**
 * The MCP address the page shows and puts into the copyable config. Beyond
 * being http(s), it has no credentials, query or fragment and only plain URL
 * characters, so what a person pastes into an app is exactly what they see.
 * The origin must be written the way the URL parser writes it, as the server
 * builds the address; that is the one place a bracketed IPv6 host
 * (http://[::1]:3365) may appear.
 */
function addressUrl(value: unknown): string | null {
  const candidate = httpUrl(value);
  if (!candidate) return null;
  const url = new URL(candidate);
  if (url.username || url.password || url.search || url.hash) return null;
  if (!candidate.startsWith(url.origin)) return null;
  const path = candidate.slice(url.origin.length);
  return /^[A-Za-z0-9._:/%-]*$/u.test(path) ? candidate : null;
}

function parseScope(value: unknown): AgentAccessScopeInfo {
  const scope = object(value);
  return {
    id: required(scope.id),
    label: text(scope.label) ?? required(scope.id),
    description: text(scope.description) ?? '',
    optional: scope.optional === true,
    ...(typeof scope.withinRights === 'boolean' ? { withinRights: scope.withinRights } : {}),
  };
}

const count = (value: unknown): number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : 0;
const mode = (value: unknown): ApprovalMode => {
  if (value === 'ask' || value === 'automatic') return value;
  throw new AgentResponseError();
};

function parseFamilyApproval(value: unknown): FamilyApproval {
  const entry = object(value);
  return {
    mode: mode(entry.mode),
    allowedCount: count(entry.allowedCount),
    ...(typeof entry.nativeTotal === 'number' ? { nativeTotal: count(entry.nativeTotal) } : {}),
  };
}

function parseApproval(value: unknown): Record<string, FamilyApproval> | undefined {
  if (!isObject(value)) return undefined;
  return Object.fromEntries(
    Object.entries(value).map(([family, entry]) => [family, parseFamilyApproval(entry)]),
  );
}

function parseGrant(value: unknown): KnowledgeClientGrant {
  const grant = object(value);
  const firstReadAt = timestamp(grant.firstReadAt);
  const status =
    grant.status === 'ready' || grant.status === 'waiting_first_read'
      ? grant.status
      : firstReadAt
        ? 'ready'
        : 'waiting_first_read';
  return {
    grantId: required(grant.grantId),
    kind: grant.kind === 'access_key' ? 'access_key' : 'oauth',
    clientId: text(grant.clientId) ?? '',
    // Shown on cards and quoted in confirmations: one bounded line, whatever
    // the agent reported.
    // Empty when the agent reported none; `withNames` fills it in the reader's language.
    clientName: displayName(grant.clientName) ?? '',
    softwareId: text(grant.softwareId),
    scopes: Array.isArray(grant.scopes)
      ? grant.scopes.filter((scope): scope is string => typeof scope === 'string')
      : [],
    companyId: text(grant.companyId) ?? '',
    companyName: text(grant.companyName) ?? '',
    createdAt: timestamp(grant.createdAt),
    firstReadAt,
    lastUsedAt: timestamp(grant.lastUsedAt),
    expiresAt: timestamp(grant.expiresAt),
    status,
    // Only an explicit false marks a grant for an earlier address; a server
    // that does not report it yet claims nothing either way.
    addressMatches: grant.addressMatches !== false,
    ...(isObject(grant.approval) ? { approval: parseApproval(grant.approval) } : {}),
    ...(typeof grant.trustAppCode === 'boolean' ? { trustAppCode: grant.trustAppCode } : {}),
  };
}

const NETWORKS = ['loopback', 'private', 'public'] as const;
const REACHABLE = ['yes', 'no', 'unchecked'] as const;
const oneOf = <T extends string>(options: readonly T[], value: unknown): T | null =>
  options.find((option) => option === value) ?? null;

/**
 * What the server says about reaching its address. Its verdict applies only
 * to the address it describes: anything else, or nothing, claims nothing.
 */
function parseAddress(value: unknown, resource: string): AgentAddress {
  const address = isObject(value) && value.url === resource ? value : {};
  return {
    url: resource,
    network: oneOf(NETWORKS, address.network),
    localOnly: address.localOnly === true,
    reachable: oneOf(REACHABLE, address.reachable) ?? 'unchecked',
  };
}

function parseAgentAccess(value: unknown): AgentAccess {
  const access = object(value);
  // Switched off on the server: nothing to set up and nothing to list.
  if (access.enabled === false)
    return {
      enabled: false,
      resource: '',
      address: { url: '', network: null, localOnly: false, reachable: 'unchecked' },
      authorizationServer: '',
      scopes: [],
      keyLifetimeDays: [],
      clients: [],
    };
  const resource = addressUrl(access.resource);
  if (!resource) throw new AgentResponseError();
  return {
    enabled: true,
    resource,
    address: parseAddress(access.address, resource),
    authorizationServer: httpUrl(access.authorizationServer) ?? new URL(resource).origin,
    scopes: Array.isArray(access.scopes) ? access.scopes.map(parseScope) : [],
    // What an access key may hold: its form and its row offer nothing beyond it.
    keyScopes: Array.isArray(access.keyScopes) ? access.keyScopes.map(parseScope) : [],
    keyLifetimeDays: Array.isArray(access.keyLifetimeDays)
      ? access.keyLifetimeDays.filter(
          (days): days is number => Number.isInteger(days) && Number(days) > 0,
        )
      : [],
    clients: listOf(access.clients, 'clients').map(parseGrant),
  };
}

// The key's fixed prefix and random part; anything else is not shown as a key.
const ACCESS_KEY = /^pnv_mcp_key_[A-Za-z0-9_-]{43}$/;

function parseCreatedKey(value: unknown): CreatedAccessKey {
  const created = object(value);
  if (typeof created.key !== 'string' || !ACCESS_KEY.test(created.key))
    throw new AgentResponseError();
  return { key: created.key, client: parseGrant(created.client) };
}

function parseAction(value: unknown): ApprovalAction {
  const action = object(value);
  const name = required(action.action);
  return {
    action: name,
    label: text(action.label) ?? name,
    undoable: action.undoable === true,
    floor: action.floor === true,
    locked: action.locked === true || action.floor === true,
    allowed: action.allowed === true,
  };
}

function parseConnection(value: unknown): ApprovalConnection | null {
  if (value === null || value === undefined) return null;
  const connection = object(value);
  const access = connection.access;
  return {
    id: required(connection.id),
    name: text(connection.name) ?? required(connection.id),
    app: text(connection.app) ?? '',
    provider: text(connection.provider) ?? '',
    audience: text(connection.audience) ?? '',
    access: access === 'read_write' || access === 'read' || access === 'off' ? access : 'off',
    logo:
      httpUrl(connection.logo) ??
      (text(connection.logo)?.startsWith('/') ? text(connection.logo) : null),
  };
}

function parseGroup(value: unknown): ApprovalGroup {
  const group = object(value);
  const actions = Array.isArray(group.actions) ? group.actions.map(parseAction) : [];
  return {
    connection: parseConnection(group.connection),
    status: group.status === 'unavailable' ? 'unavailable' : 'ok',
    actions,
    allowed: count(group.allowed),
    total: count(group.total),
    locked: count(group.locked),
  };
}

function parseApprovalActions(value: unknown): ApprovalActions {
  const body = object(value);
  return {
    family: required(body.family),
    mode: mode(body.mode),
    groups: listOf(body.groups, 'groups').map(parseGroup),
  };
}

const grantPath = (id: string) => `/api/v1/company/agent-access/${encodeURIComponent(id)}`;

/** External agents that read this company's knowledge through Poneva's MCP server. */
export const agentAccessApi = {
  list: () => companyRequest<unknown>('/api/v1/company/agent-access').then(parseAgentAccess),
  /** Replaces the grant's scopes; `knowledge:read` is always included. */
  setScopes: (grantId: string, scopes: string[]) =>
    companyRequest<unknown>(grantPath(grantId), 'PATCH', { scopes, confirmed: true }),
  /**
   * A family's actions with what is allowed. A connections family lists each
   * connection with counts only; one connection's actions are read live, so
   * they are asked for by `connectionId`, when its group opens.
   */
  approvalActions: (grantId: string, family: string, connectionId?: string) =>
    companyRequest<unknown>(
      `${grantPath(grantId)}/approval/${encodeURIComponent(family)}/actions${
        connectionId ? `?connectionId=${encodeURIComponent(connectionId)}` : ''
      }`,
    ).then(parseApprovalActions),
  /** Sets a family's mode, or adds and removes allowed actions. Returns the grant as it now stands. */
  setApproval: (grantId: string, family: string, patch: ApprovalPatch) =>
    companyRequest<unknown>(
      `${grantPath(grantId)}/approval/${encodeURIComponent(family)}`,
      'PATCH',
      { ...patch, confirmed: true },
    ).then((body) => parseGrant(object(body).client)),
  disconnect: (grantId: string) => companyRequest<void>(grantPath(grantId), 'DELETE'),
  /** A scoped, expiring key for an app that cannot sign in; shown once. */
  createKey: (input: { label: string; scopes: string[]; lifetimeDays: number }) =>
    companyRequest<unknown>('/api/v1/company/agent-access/keys', 'POST', {
      ...input,
      confirmed: true,
    }).then(parseCreatedKey),
};
